/**
 * My Activity — shows my posted jobs and my applications.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@design-system';

import { useJobStore } from '../../domains/jobs';
import { useApplicationStore } from '../../domains/applications';
import { toggleJobStatus } from '../../domains/jobs';
import { fetchApplicationsForJob, updateApplicationStatus } from '../../domains/applications';
import type { Application } from '../../domains/applications';

function MyActivityPage() {
  const navigate = useNavigate();
  const { myJobs, loading: jobsLoading, loadMyJobs } = useJobStore();
  const { myApplications, loading: appsLoading, loadMyApplications } = useApplicationStore();
  const [tab, setTab] = useState(0);
  const [toggling, setToggling] = useState<string | null>(null);

  // For viewing applications on my posted jobs
  const [selectedJobApps, setSelectedJobApps] = useState<Application[]>([]);
  const [viewingJobId, setViewingJobId] = useState<string | null>(null);
  const [appsError, setAppsError] = useState<string | null>(null);

  useEffect(() => {
    loadMyJobs();
    loadMyApplications();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleStatus = async (jobId: string, currentStatus: string) => {
    setToggling(jobId);
    try {
      await toggleJobStatus(jobId, currentStatus === 'open' ? 'closed' : 'open');
      await loadMyJobs();
    } finally {
      setToggling(null);
    }
  };

  const handleViewApplications = async (jobId: string) => {
    setViewingJobId(jobId);
    setAppsError(null);
    try {
      const apps = await fetchApplicationsForJob(jobId);
      setSelectedJobApps(apps);
    } catch (err) {
      setAppsError(err instanceof Error ? err.message : 'Failed to load applications');
    }
  };

  const handleUpdateAppStatus = async (appId: string, status: 'accepted' | 'rejected') => {
    try {
      await updateApplicationStatus(appId, status);
      if (viewingJobId) {
        await handleViewApplications(viewingJobId);
      }
    } catch {
      // Silently fail — user can retry
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success' as const;
      case 'rejected': return 'error' as const;
      default: return 'warning' as const;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        My Activity
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} className="mb-4">
        <Tab label={`My Jobs (${myJobs.length})`} />
        <Tab label={`My Applications (${myApplications.length})`} />
      </Tabs>

      {/* Tab 0: My Posted Jobs */}
      {tab === 0 && (
        <Box>
          {jobsLoading && (
            <Box className="flex justify-center py-8">
              <CircularProgress />
            </Box>
          )}

          {!jobsLoading && myJobs.length === 0 && (
            <Box className="py-8 text-center">
              <Typography color="text.secondary" gutterBottom>
                You haven&apos;t posted any jobs yet.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/jobs/new')}>
                Post Your First Job
              </Button>
            </Box>
          )}

          <Box className="flex flex-col gap-4">
            {myJobs.map((job) => (
              <Card key={job._id} variant="outlined">
                <CardContent>
                  <Box className="flex items-center justify-between">
                    <Typography variant="h6">{job.title}</Typography>
                    <Chip
                      label={job.status}
                      color={job.status === 'open' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {job.category} • {job.type} • Posted {new Date(job.createdAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/jobs/${job._id}`)}>
                    View
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleToggleStatus(job._id, job.status)}
                    disabled={toggling === job._id}
                  >
                    {job.status === 'open' ? 'Close' : 'Reopen'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleViewApplications(job._id)}
                  >
                    View Applications
                  </Button>
                </CardActions>

                {/* Inline applications viewer */}
                {viewingJobId === job._id && (
                  <Box className="px-4 pb-4">
                    {appsError && <Alert severity="error">{appsError}</Alert>}
                    {selectedJobApps.length === 0 && !appsError && (
                      <Typography variant="body2" color="text.secondary">
                        No applications yet.
                      </Typography>
                    )}
                    {selectedJobApps.map((app) => (
                      <Card key={app._id} variant="outlined" className="mb-2">
                        <CardContent className="pb-1">
                          <Box className="flex items-center justify-between">
                            <Typography variant="subtitle2">
                              {app.applicant.username} ({app.applicant.email})
                            </Typography>
                            <Chip
                              label={app.status}
                              color={statusColor(app.status)}
                              size="small"
                            />
                          </Box>
                          <Typography variant="body2" className="mt-1">
                            {app.coverNote}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Contact: {app.contactEmail} • Applied {new Date(app.createdAt).toLocaleDateString()}
                          </Typography>
                        </CardContent>
                        {app.status === 'pending' && (
                          <CardActions>
                            <Button
                              size="small"
                              color="success"
                              onClick={() => handleUpdateAppStatus(app._id, 'accepted')}
                            >
                              Accept
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleUpdateAppStatus(app._id, 'rejected')}
                            >
                              Reject
                            </Button>
                          </CardActions>
                        )}
                      </Card>
                    ))}
                  </Box>
                )}
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Tab 1: My Applications */}
      {tab === 1 && (
        <Box>
          {appsLoading && (
            <Box className="flex justify-center py-8">
              <CircularProgress />
            </Box>
          )}

          {!appsLoading && myApplications.length === 0 && (
            <Box className="py-8 text-center">
              <Typography color="text.secondary" gutterBottom>
                You haven&apos;t applied to any jobs yet.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/jobs')}>
                Browse Jobs
              </Button>
            </Box>
          )}

          <Box className="flex flex-col gap-4">
            {myApplications.map((app) => (
              <Card key={app._id} variant="outlined">
                <CardContent>
                  <Box className="flex items-center justify-between">
                    <Typography variant="h6">{app.job.title}</Typography>
                    <Chip
                      label={app.status}
                      color={statusColor(app.status)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" className="mb-1">
                    Applied {new Date(app.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2">
                    {app.coverNote}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/jobs/${app.job._id}`)}>
                    View Job
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default MyActivityPage;
